import React, { useEffect, useState } from 'react';
import { accountingAPI } from '../../Api/accounting';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, TextField, MenuItem, CircularProgress, Grid } from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Pagination } from '@mui/material';

export default function AccountingList() {
  const [transactions, setTransactions] = useState([]);
  const [rate, setRate] = useState({ rub: null, usd: null });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ type: 'income', amountBTC: '', note: '' });
  const [adding, setAdding] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(15);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchAll();
    fetchRate();
    const interval = setInterval(fetchRate, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [page, dateFrom, dateTo]);

  const fetchAll = async () => {
    setLoading(true);
    const params = {
      skip: (page - 1) * rowsPerPage,
      limit: rowsPerPage,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    };
    const res = await accountingAPI.getTransactions(params);
    setTransactions(res.data.data);
    setTotal(res.data.total);
    setLoading(false);
  };
  const fetchRate = async () => {
    const res = await accountingAPI.getRate();
    setRate(res.data.rates);
  };
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setAdding(true);
    await accountingAPI.addTransaction({ ...form, amountBTC: parseFloat(form.amountBTC) });
    setForm({ type: 'income', amountBTC: '', note: '' });
    fetchAll();
    setAdding(false);
  };

  // Итоги по текущему курсу
  const totals = transactions.reduce((acc, t) => {
    const btc = t.type === 'income' ? t.amountBTC : -t.amountBTC;
    acc.btc += btc;
    acc.rub += btc * (rate.rub || 0);
    acc.usd += btc * (rate.usd || 0);
    return acc;
  }, { btc: 0, rub: 0, usd: 0 });

  return (
    <Box sx={{ p: { xs: 1, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700, color: '#683d62' }}>
        <MonetizationOnIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Учёт (BTC, RUB, USD)
      </Typography>
      {/* Курс и фильтры */}
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CurrencyBitcoinIcon sx={{ color: '#f7931a', fontSize: 32 }} />
          <Typography variant="h6">Курс BTC → RUB:</Typography>
          {rate.rub ? (
            <Typography variant="h6" sx={{ color: '#388e3c', fontWeight: 600 }}>{rate.rub.toLocaleString()} ₽</Typography>
          ) : <CircularProgress size={20} />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CurrencyBitcoinIcon sx={{ color: '#f7931a', fontSize: 32 }} />
          <Typography variant="h6">Курс BTC → USD:</Typography>
          {rate.usd ? (
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>{rate.usd.toLocaleString()} $</Typography>
          ) : <CircularProgress size={20} />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="С даты"
            type="date"
            size="small"
            value={dateFrom}
            onChange={e => { setPage(1); setDateFrom(e.target.value); }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
          <TextField
            label="По дату"
            type="date"
            size="small"
            value={dateTo}
            onChange={e => { setPage(1); setDateTo(e.target.value); }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
        </Box>
      </Box>
      {/* Итоги */}
      <Paper sx={{ p: 2, mb: 3, background: '#f4f7fa', boxShadow: 1, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ color: '#683d62', fontWeight: 600 }}>Текущий итог:</Typography>
        <Typography variant="h6"><CurrencyBitcoinIcon sx={{ color: '#f7931a', fontSize: 20, mr: 1, verticalAlign: 'middle' }} /> {totals.btc.toFixed(8)} BTC</Typography>
        <Typography variant="h6"><MonetizationOnIcon sx={{ color: '#388e3c', fontSize: 20, mr: 1, verticalAlign: 'middle' }} /> {totals.rub.toLocaleString(undefined, { maximumFractionDigits: 2 })} ₽</Typography>
        <Typography variant="h6"><AttachMoneyIcon sx={{ color: '#1976d2', fontSize: 20, mr: 1, verticalAlign: 'middle' }} /> {totals.usd.toLocaleString(undefined, { maximumFractionDigits: 2 })} $</Typography>
      </Paper>
      {/* Форма добавления */}
      <Paper sx={{ p: 3, mb: 4, background: '#fefcfb', boxShadow: 2 }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
          <TextField select name="type" value={form.type} onChange={handleChange} label="Тип" sx={{ minWidth: 120 }}>
            <MenuItem value="income">Приход</MenuItem>
            <MenuItem value="expense">Расход</MenuItem>
          </TextField>
          <TextField name="amountBTC" value={form.amountBTC} onChange={handleChange} label="BTC" type="number" inputProps={{ step: 'any', min: 0 }} sx={{ minWidth: 120 }} required />
          <TextField name="note" value={form.note} onChange={handleChange} label="Примечание" sx={{ minWidth: 220 }} />
          <Button type="submit" variant="contained" color="primary" disabled={adding} sx={{ height: 56, fontWeight: 600, bgcolor: '#683d62', '&:hover': { bgcolor: '#4d2e4a' } }}>
            Добавить
          </Button>
        </form>
      </Paper>
      {/* Таблица */}
      <Paper sx={{ p: 2, background: '#faf7f2', boxShadow: 1 }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#683d62', fontWeight: 600 }}>История операций</Typography>
        {loading ? <CircularProgress /> : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Дата</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell>BTC</TableCell>
                    <TableCell>RUB</TableCell>
                    <TableCell>USD</TableCell>
                    <TableCell>Курс RUB</TableCell>
                    <TableCell>Курс USD</TableCell>
                    <TableCell>Примечание</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((tr) => (
                    <TableRow key={tr._id} sx={{ background: tr.type === 'income' ? '#e8f5e9' : '#ffebee' }}>
                      <TableCell>{new Date(tr.createdAt).toLocaleString()}</TableCell>
                      <TableCell style={{ color: tr.type === 'income' ? '#388e3c' : '#d32f2f', fontWeight: 600 }}>{tr.type === 'income' ? 'Приход' : 'Расход'}</TableCell>
                      <TableCell><CurrencyBitcoinIcon sx={{ color: '#f7931a', fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />{tr.amountBTC}</TableCell>
                      <TableCell><MonetizationOnIcon sx={{ color: '#388e3c', fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />{tr.amountRUB.toLocaleString()} ₽</TableCell>
                      <TableCell><AttachMoneyIcon sx={{ color: '#1976d2', fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />{tr.amountUSD ? tr.amountUSD.toLocaleString() : '-'} $</TableCell>
                      <TableCell>{tr.rateAtMoment ? tr.rateAtMoment.toLocaleString() : '-'} ₽</TableCell>
                      <TableCell>{tr.rateAtMomentUSD ? tr.rateAtMomentUSD.toLocaleString() : '-'} $</TableCell>
                      <TableCell>{tr.note}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Пагинация */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={Math.ceil(total / rowsPerPage)}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );

}
